import { useState, type ComponentType, type ReactNode } from 'react'
import { AppShell } from '@wehunt/ui'
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { AuthApi } from '../auth/AuthContext'
import { useViltrapportNavSections } from '../../features/_shell/nav'
import { useAuth } from '../auth/useAuth'
import { hasRole, useCurrentUser } from '../auth/useCurrentUser'
import { useActiveDistrict } from '../auth/useActiveDistrict'
import { useRequireLinkedAccount } from '../auth/useRequireLinkedAccount'
import { LanguageSwitcher } from '../i18n/LanguageSwitcher'
import { ImpersonationBanner } from './ImpersonationBanner'
import { QueryError } from './QueryError'
import { TopBarActions } from './TopBarActions'
import { ImpersonationSearchModal } from '../../features/administration/impersonation/ImpersonationSearchModal'

export interface AppShellLayoutProps {
  /** Optional wrapper around the routed `<Outlet/>`, injected by the
   *  composition root to bridge a guest domain's auth context (e.g. chronicle)
   *  onto clubhouse's auth without clubhouse importing that domain. */
  outletWrapper?: ComponentType<{ auth: AuthApi; children: ReactNode }>
}

export function AppShellLayout({ outletWrapper: OutletWrapper }: AppShellLayoutProps = {}) {
  const { t, i18n } = useTranslation('common')
  const { location } = useRouterState()
  const navigate = useNavigate()
  const auth = useAuth()
  const { signOut } = auth
  const currentUser = useCurrentUser()
  const isSuperAdmin = hasRole(currentUser.data, 'SuperAdmin')
  const { activeDistrictId } = useActiveDistrict()
  const navSections = useViltrapportNavSections(currentUser.data, activeDistrictId)
  // Feedback and Help both open Natlink's public contact page, localized to the
  // current UI language. Note the slug differs per language: English is
  // /language/en/contact/, Swedish is /language/sv/kontakt/.
  const contactUrl = i18n.language.startsWith('en')
    ? 'https://natlink.se/language/en/contact/'
    : 'https://natlink.se/language/sv/kontakt/'
  const [impersonateModalOpen, setImpersonateModalOpen] = useState(false)
  const link = useRequireLinkedAccount()

  async function handleSignOut() {
    await signOut()
    await navigate({ to: '/login' })
  }

  if (link.status === 'error') {
    // Without the shell there is no sidebar, so sign-out is offered here: an account
    // whose read keeps failing would otherwise leave no way to switch accounts.
    return (
      <QueryError
        error={link.error}
        onRetry={() => void link.retry()}
        secondaryAction={{ label: t('buttons.signOut'), onClick: () => void handleSignOut() }}
      />
    )
  }
  if (link.status !== 'linked') {
    // Unlinked users are being redirected to /welcome; render nothing meanwhile.
    return null
  }

  return (
    <>
      <ImpersonationBanner />
      <AppShell
        currentPath={location.pathname}
        navSections={navSections}
        logo="/assets/viltrapport-logo.svg"
        logoAlt="Viltrapport"
        onSignOut={handleSignOut}
        onSidebarNavigate={(href) => {
          if (href === '#impersonate') {
            setImpersonateModalOpen(true)
            return
          }
          if (href === '#contact-support') {
            window.open(contactUrl, '_blank', 'noopener,noreferrer')
            return
          }
          void navigate({ to: href })
        }}
        topBarShowSearch={false}
        topBarShowCart={false}
        topBarSlotRight={
          <TopBarActions
            feedbackUrl={contactUrl}
            helpUrl={contactUrl}
            supportHref="/support/users"
            isSuperAdmin={isSuperAdmin}
          />
        }
        sidebarLabels={{
          closeMenu: t('sidebar.closeMenu'),
          signOut: t('buttons.signOut'),
        }}
        topBarAriaLabels={{
          openNav: t('topbar.openNav'),
          openMenu: t('topbar.openMenu'),
          search: t('topbar.search'),
          notifications: t('topbar.notifications'),
          cart: t('topbar.cart'),
        }}
        sidebarSlotBottom={<LanguageSwitcher />}
      >
        {OutletWrapper ? (
          <OutletWrapper auth={auth}>
            <Outlet />
          </OutletWrapper>
        ) : (
          <Outlet />
        )}
      </AppShell>
      <ImpersonationSearchModal
        isOpen={impersonateModalOpen}
        onClose={() => setImpersonateModalOpen(false)}
      />
    </>
  )
}
